import api from "@/api";
import { defineStore } from "pinia";
import { useUserStore } from "@/stores/userStore";
import {
  ActivityLog,
  ActivityLogFilterOption,
  ActivityLogFilterState,
  ActivityLogQueryResponse,
} from "@/interfaces/activity-log.interface";

const HARDCODED_ACTION_TYPES = [
  "create",
  "delete",
  "update",
  "assign",
  "system-trigger",
  "read",
  'resend-invite',
  'password-reset',
  "export",
  "login"
];

const HARDCODED_ENTITY_TYPES = [
  "user",
  "bulkUpload",
  "admin",
  "class-group",
  "school-group",
  "school",
  "public-api-key"
];

const INTERNAL_ADMIN_ROLE_NAME = "Internal Admin";

type AdminOptionSource = {
  _id?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  designation?: string;
  roles?: Array<{ name?: string }>;
};

const isInternalAdminByRole = (admin?: AdminOptionSource) =>
  Array.isArray(admin?.roles) &&
  admin!.roles!.some((r) => r?.name === INTERNAL_ADMIN_ROLE_NAME);

const toAdminOption = (
  admin: AdminOptionSource,
  group: "school-admins" | "internal-admins"
): ActivityLogFilterOption | null => {
  if (!admin._id) {
    return null;
  }

  return {
    id: admin._id,
    name: `${admin.firstName || ""} ${admin.lastName || ""}`.trim() || admin.email || admin._id,
    group,
  };
};

const dedupeOptionsById = (options: ActivityLogFilterOption[]) =>
  Array.from(new Map(options.map((option) => [option.id, option])).values());

const toSingleSelection = (values: string[]) => (values.length > 0 ? [values[0]] : []);

type GenericActivityEventPayload = {
  school?: string | null;
  entityId?: string;
  entityType: string;
  actionType: string;
  details?: {
    note?: string;
    bulk?: boolean;
    metadata?: Record<string, unknown>;
    before?: unknown;
    after?: unknown;
  };
};

export const useActivityLogStore = defineStore({
  id: "activityLogStore",
  state: () => ({
    loading: false,
    loadingMore: false,
    activityLogs: [] as ActivityLog[],
    logs: [] as ActivityLog[],
    selectedSchoolId: "",
    adminOptions: [] as ActivityLogFilterOption[],
    actionTypeOptions: [...HARDCODED_ACTION_TYPES] as string[],
    entityTypeOptions: [...HARDCODED_ENTITY_TYPES] as string[],
    filters: {
      adminIds: [],
      actionTypes: [],
      entityTypes: [],
      startDate: "",
      endDate: "",
      includeAllSchoolsForAdmin: false,
    } as ActivityLogFilterState,
    pagination: {
      total: 0,
      skip: 0,
      limit: 50,
      hasMore: false,
    },
  }),
  getters: {
    getActivityLogs(): ActivityLog[] {
      return this.activityLogs;
    },
  },
  actions: {
    setSelectedSchool(schoolId: string) {
      this.selectedSchoolId = schoolId;
    },

    setFilters(filters: Partial<ActivityLogFilterState>) {
      const nextFilters = {
        ...this.filters,
        ...filters,
      };

      nextFilters.adminIds = toSingleSelection(nextFilters.adminIds);
      nextFilters.actionTypes = toSingleSelection(nextFilters.actionTypes);
      nextFilters.entityTypes = toSingleSelection(nextFilters.entityTypes);

      this.filters = nextFilters;
    },

    async logGenericActivityEvent(payload: GenericActivityEventPayload): Promise<boolean> {
      try {
        const response = await api.post("/v2/access/activity-logs/event", payload);
        return Boolean(response?.data?.success);
      } catch (error) {
        console.error("Error logging generic activity event:", error);
        return false;
      }
    },

    async fetchAdminOptionsBySchool() {
      const userStore = useUserStore();
      const selectedAdminId = this.filters.adminIds[0] || "";
      const previousSelectedOption = selectedAdminId
        ? this.adminOptions.find((option) => option.id === selectedAdminId) || null
        : null;

      try {
        const internalResponse = await api
          .get("/v2/access/admin/all")
          .catch(() => ({ data: { success: false, data: [] } }));
        const internalAdminsRaw: AdminOptionSource[] = internalResponse.data.success
          ? internalResponse.data.data || []
          : [];

        const internalAdmins = internalAdminsRaw
          .filter((admin) => isInternalAdminByRole(admin))
          .map((admin) => toAdminOption(admin, "internal-admins"))
          .filter((admin): admin is ActivityLogFilterOption => Boolean(admin));

        const fallbackInternal = isInternalAdminByRole(userStore.user || undefined)
          ? toAdminOption(userStore.user || {}, "internal-admins")
          : null;

        if (!this.selectedSchoolId) {
          const nextOptions = dedupeOptionsById(
            fallbackInternal ? [...internalAdmins, fallbackInternal] : internalAdmins
          );

          this.adminOptions =
            this.filters.includeAllSchoolsForAdmin && previousSelectedOption
              ? dedupeOptionsById([...nextOptions, previousSelectedOption])
              : nextOptions;

          if (
            !this.filters.includeAllSchoolsForAdmin &&
            this.filters.adminIds[0] &&
            !this.adminOptions.some((option) => option.id === this.filters.adminIds[0])
          ) {
            this.filters.adminIds = [];
          }

          return;
        }

        const schoolResponse = await api
          .get(`/v2/organisation/school-admins/school/${this.selectedSchoolId}`)
          .catch(() => ({ data: { success: false, data: [] } }));

        const schoolAdminsRaw: AdminOptionSource[] = schoolResponse.data.success
          ? schoolResponse.data.data || []
          : [];

        const schoolAdmins = schoolAdminsRaw
          .filter((admin) => !isInternalAdminByRole(admin))
          .map((admin) => toAdminOption(admin, "school-admins"))
          .filter((admin): admin is ActivityLogFilterOption => Boolean(admin));

        const nextOptions = dedupeOptionsById(
          fallbackInternal
            ? [...schoolAdmins, ...internalAdmins, fallbackInternal]
            : [...schoolAdmins, ...internalAdmins]
        );

        this.adminOptions =
          this.filters.includeAllSchoolsForAdmin && previousSelectedOption
            ? dedupeOptionsById([...nextOptions, previousSelectedOption])
            : nextOptions;

        if (
          !this.filters.includeAllSchoolsForAdmin &&
          this.filters.adminIds[0] &&
          !this.adminOptions.some((option) => option.id === this.filters.adminIds[0])
        ) {
          this.filters.adminIds = [];
        }
      } catch (error) {
        console.error("Error fetching admins for activity logs:", error);
        this.adminOptions = [];
      }
    },

    async fetchLogs(reset = true) {
      if (reset) {
        this.loading = true;
      } else {
        this.loadingMore = true;
      }

      try {
        const skip = reset ? 0 : this.logs.length;
        const shouldIgnoreSchoolScope = this.filters.includeAllSchoolsForAdmin;

        const payload = {
          schoolId: shouldIgnoreSchoolScope ? undefined : this.selectedSchoolId,
          includeAllSchoolsForAdmin: shouldIgnoreSchoolScope,
          adminIds: this.filters.adminIds,
          actionTypes: this.filters.actionTypes,
          entityTypes: this.filters.entityTypes,
          startDate: this.filters.startDate,
          endDate: this.filters.endDate,
          limit: this.pagination.limit,
          skip,
        };

        const response = await api.post("/v2/access/activity-logs/query", payload);

        if (!response.data.success) {
          throw new Error("Failed to fetch activity logs");
        }

        const data: ActivityLogQueryResponse = response.data.data;
        const fetchedLogs = data.logs || [];

        this.logs = reset ? fetchedLogs : [...this.logs, ...fetchedLogs];
        this.pagination = data.pagination;

        this.actionTypeOptions = [...HARDCODED_ACTION_TYPES];
        this.entityTypeOptions = [...HARDCODED_ENTITY_TYPES];
      } catch (error) {
        console.error("Error fetching activity logs:", error);
        if (reset) {
          this.logs = [];
        }
      } finally {
        this.loading = false;
        this.loadingMore = false;
      }
    },

    async fetchActivityLogs() {
      this.loading = true;

      try {
        const response = await api.get("/v2/access/activity-logs/all");

        if (response.status !== 200) {
          throw new Error("Failed to fetch activity logs from API.");
        }

        this.activityLogs = response.data.data || [];
      } catch (error) {
        console.error("Error fetching activity logs:", error);
      } finally {
        this.loading = false;
      }
    },

    async showMore() {
      if (this.loading || this.loadingMore || !this.pagination.hasMore) {
        return;
      }

      await this.fetchLogs(false);
    },
  },
  persist: true,
});
